import * as React from 'react';

import { FC, PropsWithChildren } from 'react';

import { useDeepCompareMemo } from 'use-deep-compare';

export type CustomProperties = Record<string, any>;

type ValidatedFormContextType = {
  inputWrapper: (rawInput, object) => any;
  outputWrapper: (output: React.ReactNode) => React.ReactNode;
  customProperties?: CustomProperties;
};

type ValidatedFormProviderType = {
  inputWrapper: (rawInput, object, customProperties?: CustomProperties) => any;
  outputWrapper: (
    output: React.ReactNode,
    customProperties?: CustomProperties
  ) => React.ReactNode;
  customProperties?: CustomProperties;
};

const ValidatedFormContext = React.createContext<
  ValidatedFormContextType | undefined
>(undefined);

const ValidatedFormProvider: FC<
  PropsWithChildren<ValidatedFormProviderType>
> = ({ children, inputWrapper, outputWrapper, customProperties }) => {
  const outputWrapperWithCustomProperties = React.useCallback(
    (output: React.ReactNode) => {
      return outputWrapper(output, customProperties);
    },
    [outputWrapper, customProperties]
  );

  const inputWrapperWithCustomProperties = React.useCallback(
    (rawinput: React.ReactNode, object: any) => {
      return inputWrapper(rawinput, object, customProperties);
    },
    [inputWrapper, customProperties]
  );
  const value: ValidatedFormContextType = useDeepCompareMemo(
    () => ({
      outputWrapper: outputWrapperWithCustomProperties,
      inputWrapper: inputWrapperWithCustomProperties,
      customProperties,
    }),
    [
      inputWrapperWithCustomProperties,
      outputWrapperWithCustomProperties,
      customProperties,
    ]
  );

  return (
    <ValidatedFormContext.Provider value={value}>
      {children}
    </ValidatedFormContext.Provider>
  );
};

const useValidatedForm = (): ValidatedFormContextType | undefined => {
  const context = React.useContext(ValidatedFormContext);

  return context;
};

export { useValidatedForm, ValidatedFormProvider };
